import React from 'react'

import Button from 'Components/Button'
import Checkbox from 'Components/Checkbox'
import Input from 'Components/Input'
import { SVGArrow, SVGTrashSmall } from 'Components/SVGCollection'

import cn from 'classnames'
import cg from 'Scss/app.scss'
import btnCss from 'Components/Button/Button.scss'
import prnt from 'Components/Profile/ProfileNavListItem/ProfileNavListItem.scss'
import cl from './AddressListFoldableAddressForm.scss'
import cla from '../ShippingAddresses/Addresses.scss'
import Form from 'Components/Form'
import { NEW_ADDRESS_ID, formatAddress, processPostcodes, formatAddressWithName } from 'Utils/helpers/addressHelper'
import { ShippingAddressFragment, postcodes, postcodesVariables } from 'Utils/api/gql/types'
import _app from 'Pages/_app'
import postCodes from 'Utils/api/gql/queries/postCodes'
import InputWrapper from 'Components/InputWrapper'
import { isEqual } from 'Utils/helpers/misc'
import DropdownSelect from 'Components/Dropdown/DropdownSelect'
import Badge from '../ProfileNavListItem/Badge'

type OwnProps = {
  onChange: (address: ShippingAddressFragment) => void
  onValidPostcodeEntered?: (postcode: number, city: string) => void
  opened?: boolean
  address: ShippingAddressFragment
  isFetching?: boolean
  hideDeleteButton?: boolean
  postCodeDisabled?: boolean
  noDropDown?: boolean
  errors: Redux.IFormErrors<ShippingAddressFragment>
} & ({
  onClick: () => void
  onSaveClick: () => void
  onResetClick: () => void
  onDeleteClick: () => void
  fieldsOnly?: false | undefined
} | {
  onClick?: () => void
  onSaveClick?: () => void
  onResetClick?: () => void
  onDeleteClick?: () => void
  fieldsOnly: true
})

type PropType = OwnProps & {divRef: React.Ref<HTMLDivElement>}
type StateType = {
  cities: string[]
  streets: string[]
  postcodeFetching: boolean
  postcodeError: string | undefined
}

class ShippingAddressForm extends React.Component<PropType, StateType> {
  timeout: number | null = null
  state: StateType = {
    cities: [],
    streets: [],
    postcodeFetching: false,
    postcodeError: undefined
  }

  updateCitiesByPostcode = async (postcode: string | number) => {
    postcode = Number(postcode)
    if (postcode < 1000 || postcode > 9999) {
      return
    }

    this.setState({ postcodeFetching: true })

    try {
      const result = await _app.apolloClient.query<postcodes, postcodesVariables>({
        query: postCodes,
        variables: { filters: { postcode } }
      })

      const { postcodes } = result.data
      const { cities, streets } = processPostcodes(postcodes)

      let postcodeError: string | undefined
      let newAddress = {...this.props.address}

      if (cities.length === 0) {
        postcodeError = 'Nem létező irányítószám!'
      } else if (!cities.includes(this.props.address.city)) {
        newAddress = { ...newAddress, city: cities[0], street: '' }
      }

      if (streets.length > 0 && !streets.includes(newAddress.street)) {
        newAddress.street = ''
      }

      if (!isEqual(this.props.address, newAddress)){
        this.props.onChange(newAddress)
      }

      if (!postcodeError && this.props.onValidPostcodeEntered) {
        this.props.onValidPostcodeEntered(postcode, cities.join(', '))
      }

      this.setState({ cities, streets, postcodeError })
    } catch (e) {
      this.setState({ cities: [], streets: [], postcodeError: 'Ismeretlen hiba történt!' })
    }
    this.setState({ postcodeFetching: false })
  }

  componentDidMount() {
    const { postcode } = this.props.address

    this.updateCitiesByPostcode(postcode)
  }

  componentDidUpdate(prevProps: PropType) {
    if (prevProps.address.postcode !== this.props.address.postcode) {
      this.updateCitiesByPostcode(this.props.address.postcode)
    }
    if (this.state.cities.length === 1 && this.props.address.city !== this.state.cities[0]) {
      this.props.onChange({ ...this.props.address, city: this.state.cities[0] })    // force set city if only one available
    }
  }

  handleChange = async (name: keyof ShippingAddressFragment, value: string | boolean | null) => {
    this.props.onChange({
      ...this.props.address,
      [name]: value
    })
  }

  renderForm = (isNewAddress?: boolean, isFetching?: boolean, isValidPostcode?: boolean, isGoodId?: boolean) =>
    <Form onChange={this.handleChange} values={this.props.address} errors={this.props.errors}>
      <fieldset>
        <div className={cn(cg.gridX, cg.gridMarginX)}>
          {isGoodId && <div className={cg.cell}>
            <strong>A GoodID címek csak a GoodID felületén keresztül módosíthatóak!</strong>
          </div>}
          <div className={cn(cg.cell, cg.medium12, cg.padding)}>
            <Input name="name" label="Név *" type="text" autoComplete="name" placeholder="" required textOnly={isGoodId} />
          </div>
          <div className={cn(cg.cell, cg.medium12)}>
              <Input name="postcode" label="Irányítószám *" type="text" placeholder="1111" required textOnly={isGoodId || this.props.postCodeDisabled} />
          </div>
          <div className={cn(cg.cell, cg.medium12)}>
            <InputWrapper
              name='city'
              label='Település *'
            >
            {(this.state.cities.length === 1 || isGoodId)
              ? <span className={cl.readonlyText}>{this.state.cities[0]}</span>
              : <DropdownSelect
                  items={this.state.cities.map(city => ({ title: city, value: city }))}
                  placeholder="Válassz települést!"
                  name="city"
                  noResultsMessage="Nincs találat"
                  isNullable={false}
                  disabled={!isValidPostcode}
                />
            }
            </InputWrapper>
          </div>

          <div className={cn(cg.cell, cg.medium12)}>
            {!isGoodId && (this.state.streets.length || this.state.postcodeFetching)
              ? <InputWrapper
                name='street'
                label='Közterület *'
              >
                <DropdownSelect
                  items={this.state.streets.map(street => ({ title: street, value: street }))}
                  placeholder="Válassz közterületet!"
                  name="street"
                  isNullable={false}
                  noResultsMessage="Nincs találat"
                  disabled={!isValidPostcode}
                  maxListHeight={222}
                />
              </InputWrapper>
              : <Input name="street" label="Közterület *" type="text" placeholder="Kossuth Lajos u." disabled={!isValidPostcode} required textOnly={isGoodId} />
            }

          </div>

          <div className={cn(cg.cell, cg.medium12)}>
            <Input name="house_number" label="Házszám *" type="text" placeholder="123" disabled={!isValidPostcode} required textOnly={isGoodId} />
          </div>

          <div className={cn(cg.cell, cg.medium12)}>
            <Input name="telephone" label="Telefonszám *" type="phone" placeholder="201234567" textOnly={isGoodId} />
          </div>

          <div className={cg.cell}>
            <Input name="note" type="textarea" label="Megjegyzés" placeholder="pl: kapucsengő" textOnly={isGoodId} />
          </div>
          { !this.props.fieldsOnly && !isGoodId &&
            <>
              <div className={cn(cg.cell)}>
                <div className={cn(cg.gridX, cg.gridMarginX, cg.alignMiddle)}>

                  <div className={cn(cg.cell, cg.small6, cg.textCenter, cg.mediumTextRight)}>
                    <Button className={cn(btnCss.hasMarginBottom, btnCss.largeWidth, btnCss.hollow)} label="Mégsem" onClick={this.props.onResetClick} disabled={isFetching} />
                  </div>

                  <div className={cn(cg.cell, cg.small6, cg.textCenter, cg.mediumTextLeft)}>
                    <Button className={cn(btnCss.hasMarginBottom, btnCss.largeWidth, btnCss.secondary)} label={!isNewAddress ? "Módosítás" : "Mentés"} onClick={this.props.onSaveClick} disabled={isFetching} />
                  </div>
                </div>

                {!isNewAddress && !this.props.hideDeleteButton && <div className={cn(cg.gridX, cg.gridMarginX, cl.marginTop)}><div className={cn(cg.cell, cg.small12, cg.textCenter)}>
                  <Button
                    className={cn(btnCss.delete, btnCss.link)}
                    label="Törlés"
                    onClick={this.props.onDeleteClick}
                    renderIcon={{ ariaLabel: 'ariaLabel', Icon: <SVGTrashSmall width={21} height={21} title="Törlés" /> }}
                    disabled={isFetching}
                  />
                </div></div>}
              </div>
            </>
          }
          { !this.props.fieldsOnly && isGoodId && 
          <div className={cn(cg.cell)}>
              <div className={cn(cg.gridX, cg.gridMarginX, cg.alignMiddle)}>
              <div className={cn(cg.cell, cg.small4)}></div>
                <div className={cn(cg.cell, cg.small4)}>
                    <Button
                      className={cn(btnCss.secondary, btnCss.expanded)}
                      label="Bezárás"
                      onClick={this.props.onResetClick}
                      disabled={isFetching}
                    />
                </div>
                <div className={cn(cg.cell, cg.small4)}></div>
              </div>
            </div>
          }
        </div>
      </fieldset>
    </Form>



  render() {
    const { address, opened, isFetching } = this.props
    const isNewAddress = address.id === NEW_ADDRESS_ID
    const isValidPostcode = this.state.cities.length > 0
    let formErrors = this.props.errors
    if (this.state.postcodeError) {
      formErrors = { ...formErrors, fieldErrors: {...formErrors.fieldErrors, postcode: this.state.postcodeError } }
    }

    if (this.props.fieldsOnly) {
      return this.renderForm(isNewAddress, isFetching, isValidPostcode, this.props.address.is_goodid)
    }

    const displayAddress = !isNewAddress ? formatAddressWithName(address) : 'Új szállítási cím'

    return (
      <div className={!this.props.noDropDown ? cn(prnt.profileNav__listItem, cl.profileNav__listItemIsFoldable, cla.profileNav__listItem, { [cl.profileNav__listItemIsOpened]: opened }) : ''} ref={this.props.divRef}>
        <h3 className={cn(cg.gridX, cg.alignMiddle, prnt.listItem__gridX, { [cl.listItemIsFoldable__header]: !this.props.noDropDown })} onClick={this.props.onClick}>
          <span className={cn(cg.cell, cg.auto, prnt.cell, prnt.auto)}>
            {displayAddress}
          </span>

          {address.is_goodid && <Badge label="GoodID" />}

          {!this.props.noDropDown && <span className={cn(cg.cell, cg.shrink, prnt.cell, prnt.shrink, cl.arrowWrapper)}>
            <span aria-hidden={true}>
              <SVGArrow width={24} height={24} />
            </span>
          </span>}
        </h3>

        <div className={cn(cg.cell, cg.medium8, cg.large9, cl.listItemIsFoldable__content)}>
          {this.renderForm(isNewAddress, isFetching, isValidPostcode, this.props.address.is_goodid)}
        </div>
      </div>
    )
  }
}

export default React.forwardRef<HTMLDivElement, OwnProps>((props: OwnProps, ref) => <ShippingAddressForm divRef={ref} {...props} />)
